//@flow

import React, { Component } from 'react';
import { View, StyleSheet, ScrollView, Keyboard } from 'react-native';
import { Text } from 'trpc-react-native-ui';
import { Field } from 'redux-form';

import { FullButton, HoshiInput, Checkbox } from '../../../../components/common';
import FormatService from '../../../../lib/format';
import I18n from '../../../../lib/i18n';
import config from '../../../../config';
import theme from '../../../../theme-config';

import type { NavigationRoute, NavigationScreenProp } from '@react-navigation/compat';

class KeepNumber extends Component<Props, State> {
  styles = getStyles();
  RIONumberComponent = null;
  state = {
    keepNumberCheckboxValue: false,
  };

  cheatCode = () => {
    const randomBetween = (min, max) => Math.floor(Math.random() * (max - min)) + min;
    // 4 means it will be active after 4 hours, and you don't want to finish with 0 else the activation will fail
    const number = '0696' + randomBetween(1000, 9999) + '4' + randomBetween(1, 9);
    this.props.change('phoneNumber', number);

    if (config.isRioFieldDisplayed) {
      this.props.change('RIONumber', '01P09SA09098');
    }

    this.props.change('cheatCode', true);

    this.props.handleSubmit();
  };

  focusOnRIONumberComponent = () => this.RIONumberComponent && this.RIONumberComponent.getRenderedComponent().focus();

  getPhoneNumberConfig = (isRioFieldDisplayed: boolean) =>
    isRioFieldDisplayed
      ? {
          returnKeyType: 'next',
          onSubmitEditing: this.focusOnRIONumberComponent,
        }
      : { returnKeyType: 'go', onSubmitEditing: this.submit };

  renderInfoPanel = (isRioFieldDisplayed: boolean) =>
    isRioFieldDisplayed && (
      <View style={this.styles.infoTextContainer}>
        <Text style={[this.styles.infoText, this.styles.infoTitle]}>
          {I18n.t('SignUp.ChooseNumber.infoPanel.title')}
        </Text>
        <Text style={[this.styles.infoText]}>{I18n.t('SignUp.ChooseNumber.infoPanel.content')}</Text>
      </View>
    );

  submit = () => {
    if ((config.isRioFieldDisplayed && this.state.keepNumberCheckboxValue) || !config.isRioFieldDisplayed) {
      this.props.handleSubmit();
    }
  };

  rioNumberFieldConfig = () => {
    if (this.state.keepNumberCheckboxValue) {
      return { returnKeyType: 'go', onSubmitEditing: this.submit };
    }
    return { returnKeyType: 'next', onSubmitEditing: Keyboard.dismiss };
  };

  render() {
    const styles = this.styles;
    const phoneNumberFieldConfig = this.getPhoneNumberConfig(config.isRioFieldDisplayed);
    const rioFieldConfig = this.rioNumberFieldConfig();
    const isValidationButtonActivate = config.isRioFieldDisplayed ? !this.state.keepNumberCheckboxValue : false;
    return (
      <ScrollView style={styles.container} keyboardShouldPersistTaps="handled">
        <View style={styles.localPhoneRow}>
          <Text style={styles.indicative}>{this.props.localPhoneIndicative}</Text>
          <View style={styles.fieldContainerIfIndicative}>
            <Field
              name="phoneNumber"
              component={HoshiInput}
              keyboardType="phone-pad"
              placeholder={config.phoneNumberExample || I18n.t('phoneNumber')}
              returnKeyType={phoneNumberFieldConfig.returnKeyType}
              maxLength={14}
              style={styles.inputContainerIfIndicative}
              inputStyle={styles.input}
              onSubmitEditing={phoneNumberFieldConfig.onSubmitEditing}
              errorNumberOfLines={3}
              wrapperContainerStyle={styles.inputContainerStyle}
              format={FormatService.formatPhone}
              testID={'keptPhoneNumber'}
            />
          </View>
        </View>
        {config.isRioFieldDisplayed && (
          <>
            <Field
              ref={ref => (this.RIONumberComponent = ref)}
              forwardRef
              name="RIONumber"
              component={HoshiInput}
              keyboardType="default"
              errorNumberOfLines={2}
              placeholder={I18n.t('rioNumber')}
              style={styles.inputContainer}
              inputStyle={styles.input}
              testID={'keptPhoneNumberRioNumber'}
              returnKeyType={rioFieldConfig.returnKeyType}
              onSubmitEditing={rioFieldConfig.onSubmitEditing}
              autoCapitalize="characters"
            />
            <Field
              name="keepNumberCheckbox"
              component={({ input: { value, onChange } }) => (
                <Checkbox
                  style={styles.checkbox}
                  labelStyle={styles.labelStyle}
                  text={I18n.t('SignUp.ChooseNumber.keepThisNumberTerm')}
                  onPress={() => {
                    this.setState({ keepNumberCheckboxValue: !value });
                    onChange(!value);
                  }}
                  isChecked={this.state.keepNumberCheckboxValue}
                  lightColors
                  testID={'keepNumberCheckbox'}
                />
              )}
            />
          </>
        )}
        <FullButton
          text={I18n.t('SignUp.ChooseNumber.keepThisNumber')}
          onPress={this.submit}
          onLongPress={config.areCheatCodesEnabled && this.cheatCode}
          style={styles.button}
          isLoading={this.props.isLoading}
          testID={'keptPhoneNumberButton'}
          disabled={isValidationButtonActivate}
        />
        {this.renderInfoPanel(config.isRioFieldDisplayed)}
      </ScrollView>
    );
  }
}

type State = {
  keepNumberCheckboxValue: boolean,
};

type Props = {
  change: Function,
  handleSubmit: Function,
  isLoading: boolean,
  localPhoneIndicative: string,
  navigation: NavigationScreenProp<NavigationRoute>,
  phoneNumber: ?string,
};

const getStyles = () =>
  StyleSheet.create({
    container: {
      paddingTop: 16,
      paddingHorizontal: 10,
      marginHorizontal: 10,
      marginBottom: 20,
      flex: 1,
    },
    inputContainer: {
      marginVertical: 8,
      marginHorizontal: '10%',
    },
    input: {
      fontWeight: '300',
      textAlign: 'center',
    },
    button: {
      marginBottom: 25,
    },
    infoTextContainer: {
      alignItems: 'center',
      marginVertical: 15,
      paddingBottom: 10,
    },
    infoTitle: {
      textAlign: 'center',
      fontWeight: 'bold',
    },
    infoText: {
      textAlign: 'justify',
      fontWeight: '100',
      fontSize: 12,
      color: theme.defaultTextColor,
    },
    labelStyle: {
      fontSize: 12,
      color: theme.defaultTextColor,
      textAlign: 'justify',
      fontWeight: '300',
    },
    checkbox: {
      marginBottom: 25,
      marginTop: 12,
    },
    localPhoneRow: {
      marginHorizontal: '10%',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    indicative: {
      fontSize: 16,
      marginBottom: 10,
    },
    inputContainerIfIndicative: {
      width: '100%',
      marginVertical: 8,
    },
    fieldContainerIfIndicative: {
      width: '80%',
    },
    inputContainerStyle: {
      justifyContent: 'flex-start',
      alignItems: 'flex-end',
    },
  });

export default KeepNumber;
